import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import { Company, CompanyMember, Invitation, MembershipRequest, PaginatedResponse } from "@/types/api";

export const useCompanies = (page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: ["companies", { page, pageSize }],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Company>>("/companies/", {
        params: { page, page_size: pageSize },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });
};

export const useCompany = (id: string | undefined) => {
  return useQuery({
    queryKey: ["companies", id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get<Company>(`/companies/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useCompanyMembers = (id: string | undefined, page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: ["companies", id, "members", { page, pageSize }],
    queryFn: async () => {
      if (!id) return { items: [], total: 0, page: 1, page_size: 10, pages: 0 };
      const { data } = await api.get<PaginatedResponse<CompanyMember>>(
        `/companies/${id}/members`,
        { params: { page, page_size: pageSize } }
      );
      return data;
    },
    enabled: !!id,
    placeholderData: (prev) => prev,
  });
};

export const useInviteMember = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; role: string }) => {
      const { data } = await api.post<Invitation>(`/companies/${companyId}/invitations`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "invitations"] });
    },
  });
};

export const useCompanyInvitations = (companyId: string | undefined, page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: ["companies", companyId, "invitations", { page, pageSize }],
    queryFn: async () => {
      if (!companyId) return { items: [], total: 0, page: 1, page_size: 10, pages: 0 };
      const { data } = await api.get<PaginatedResponse<Invitation>>(
        `/companies/${companyId}/invitations`,
        { params: { page, page_size: pageSize } }
      );
      return data;
    },
    enabled: !!companyId,
    placeholderData: (prev) => prev,
  });
};

export const useRemoveMember = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      await api.delete(`/companies/${companyId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "members"] });
    },
  });
};

export const useRevokeInvitation = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: number) => {
      await api.delete(`/companies/${companyId}/invitations/${invitationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "invitations"] });
    },
  });
};

export interface CompanyStats {
  total_conversions: number;
  total_valor_bruto_reais: number;
  total_valor_bruto_pontos: number;
  conversoes_reais: number;
  conversoes_pontos: number;
}

export const useCompanyStats = (companyId: number | string | undefined) => {
  return useQuery<CompanyStats>({
    queryKey: ["companies", String(companyId), "stats"],
    queryFn: async () => {
      const { data } = await api.get<CompanyStats>(`/companies/${companyId}/stats`);
      return data;
    },
    enabled: !!companyId,
  });
};

export const useRequestMembership = () => {
  return useMutation({
    mutationFn: async (companyId: number) => {
      const { data } = await api.post<MembershipRequest>(`/companies/${companyId}/membership-requests`);
      return data;
    },
  });
};

export const useMembershipRequestPreview = (token: string | undefined) => {
  return useQuery<MembershipRequest>({
    queryKey: ["membership-requests", token],
    queryFn: async () => {
      const { data } = await api.get<MembershipRequest>(`/companies/membership-requests/preview/${token}`);
      return data;
    },
    enabled: !!token,
  });
};

export const useAcceptMembershipRequest = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await api.post<CompanyMember>(`/companies/membership-requests/${token}/accept`);
      return data;
    },
  });
};

export const useDeclineMembershipRequest = () => {
  return useMutation({
    mutationFn: async ({ token, message }: { token: string; message?: string }) => {
      await api.post(`/companies/membership-requests/${token}/decline`, { message: message ?? null });
    },
  });
};

export const useUpdateCompany = (companyId: number | null | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.patch<Company>(`/companies/${companyId}`, { name });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", String(companyId)] });
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });
};
